import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { HeartHandshake, AlertCircle } from 'lucide-react';
import './EventRegistration.css';
import { INVITE_STORAGE_KEY, INVITE_DETAILS_KEY, clearStoredInvite } from '../utils/invite';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const EventRegistration = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [invite, setInvite] = useState(null); // resolved invite | { invalid: true } | { error: true }
    const [inviteResolving, setInviteResolving] = useState(false);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/public/events/${eventNameId}`);
                setEvent(response.data);
            } catch (err) {
                setError('Event not found');
            } finally {
                setLoading(false);
            }
        };

        if (eventNameId) {
            fetchEvent();
        }
    }, [eventNameId]);

    // Nominated-nonprofit invitation link (?invite=CODE): remember it for the rest of the registration flow
    const inviteCode = searchParams.get('invite');
    const resolveInvite = useCallback(async () => {
        if (!inviteCode) return;
        setInviteResolving(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/donor-circle/public/invite/${encodeURIComponent(inviteCode)}`);
            localStorage.setItem(INVITE_STORAGE_KEY, inviteCode);
            localStorage.setItem(INVITE_DETAILS_KEY, JSON.stringify(response.data));
            setInvite(response.data);
        } catch (err) {
            if (err.response?.status === 404) {
                // The server rejected the code: drop it so it cannot leak into a later registration
                clearStoredInvite();
                setInvite({ invalid: true });
            } else {
                // Network/server problem: keep whatever is stored and let the user retry
                setInvite({ error: true });
            }
        } finally {
            setInviteResolving(false);
        }
    }, [inviteCode]);

    useEffect(() => { resolveInvite(); }, [resolveInvite]);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        if (value && !validateEmail(value)) {
            setEmailError('Please enter a valid email address');
        } else {
            setEmailError('');
        }
    };

    const handleNext = async () => {
        if (!email) {
            setEmailError('Required');
            return;
        }

        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        if (inviteResolving) return; // wait for the invite details before moving on

        setIsSubmitting(true);
        try {
            // A new registration only carries an invite if this visit came through a valid invite link;
            // otherwise forget any invite left over from an earlier, unfinished registration
            if (!invite || invite.invalid || invite.error) {
                clearStoredInvite();
            }
            const response = await axios.post(`${API_BASE_URL}/public/event-registrations/`, {
                email: email,
                event_name_id: eventNameId
            });

            // Store the EventRegistrationGuid and email in localStorage
            localStorage.setItem('eventRegistrationGuid', response.data.EventRegistrationGuid);
            localStorage.setItem('userEmail', email);

            // Navigate to the registration type selection page
            navigate(`/event/${eventNameId}/registration/type`);
        } catch (err) {
            if (err.response?.data?.detail === 'Email already registered for this event') {
                setEmailError('This email is already registered for this event');
            } else {
                setError('Registration failed. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };


    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    if (error || !event) {
        return <div className="page-error">Event not found</div>;
    }


    return (
        <div className="event-registration">
            {/* Event Banner with Logo */}
            <div className="event-banner">
                <img
                    src={event.EventImage || "/Funds4Charity.png"}
                    alt="Event Logo"
                    className="event-logo"
                />
            </div>

            {/* Event Title */}
            <div className="event-header">
                <h1 className="event-title">{event.Name}</h1>
            </div>

            {/* Event Details */}
            <div className="event-details">
                <div className="detail-item location-item">
                    <span className="detail-text">
                        <i className="fas fa-map-marker-alt detail-icon"></i>
                        {event.Location}
                        {event.Address1 && ` - ${event.Address1}`}
                        {event.City && `, ${event.City}`}
                        {event.StateProvince && `, ${event.StateProvince}`}
                        {event.Country && `, ${event.Country}`}
                    </span>
                </div>

                <div className="detail-item date-time-row">
                    <div className="date-section">
                        <i className="fas fa-calendar-alt detail-icon"></i>
                        <span className="detail-text">
                            {formatDate(event.StartDate)} - {formatDate(event.EndDate)}
                        </span>

                        <i className="fas fa-clock detail-icon"></i>
                        <span className="detail-text">
                            {formatTime(event.StartDate)} - {formatTime(event.EndDate)} {event.TimeZone || 'EDT'}
                        </span>
                    </div>

                </div>
            </div>

            {/* Separator */}
            <div className="separator"></div>

            {/* Registration Section */}
            <div className="registration-section">
                {invite && !invite.invalid && !invite.error && (
                    <div className="invite-banner">
                        <HeartHandshake size={16} className="invite-banner-icon" aria-hidden="true" />
                        <div>
                            <strong>You&rsquo;ve been nominated.</strong> {invite.nominated_by ? `${invite.nominated_by} nominated ` : 'A Donor Circle member nominated '}
                            <strong>{invite.nominee_name}</strong> to join {invite.event_name || 'this event'}. Register below with your work email to set up your organization&rsquo;s profile.
                        </div>
                    </div>
                )}
                {invite?.invalid && (
                    <div className="invite-banner invalid" role="alert">
                        <AlertCircle size={16} className="invite-banner-icon" aria-hidden="true" />
                        <div>This invitation link is no longer valid. You can still register below, or contact service@funds4charity.org.</div>
                    </div>
                )}
                {invite?.error && (
                    <div className="invite-banner error" role="alert">
                        <AlertCircle size={16} className="invite-banner-icon" aria-hidden="true" />
                        <div>
                            We couldn&rsquo;t check your invitation right now.{' '}
                            <button type="button" className="invite-retry-btn" onClick={resolveInvite} disabled={inviteResolving}>
                                {inviteResolving ? 'Checking…' : 'Try again'}
                            </button>
                        </div>
                    </div>
                )}
                <h2 className="registration-prompt">Register with your email address</h2>

                <div className="form-group">
                    <label htmlFor="email" className="form-label">
                        Work email <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={handleEmailChange}
                        className={`form-input ${emailError ? 'error' : ''}`}
                        placeholder="name@organization.org"
                        autoComplete="email"
                        required
                        aria-invalid={!!emailError}
                        aria-describedby={emailError ? 'email-error' : undefined}
                        disabled={isSubmitting || inviteResolving}
                    />
                    {emailError && <span id="email-error" className="error-message" role="alert">{emailError}</span>}
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="navigation-buttons">
                <button
                    type="button"
                    className={`next-button ${email ? 'active' : ''}`}
                    onClick={handleNext}
                    disabled={!email || emailError}
                >
                    {isSubmitting ? 'Submitting...' : (inviteResolving ? 'Checking invitation…' : 'Next')}
                </button>
            </div>
        </div >
    );
};

export default EventRegistration;